/**
 * Estilos do catálogo de relatórios.
 *
 * Este arquivo existe por um motivo concreto, e não por gosto: o `CssBaseline`
 * do tema global (`src/styles/themes/default-theme.tsx:80-186`) aplica regras
 * de plataforma inteira que atropelam o `sx` de qualquer componente —
 *
 *   `.MuiOutlinedInput-root { height: 56px; padding: 16px 14px }`
 *   `.MuiInputLabel-root    { font-size: 18px !important; background: #FAFAFA !important }`
 *   `.MuiButton-contained   { background-color: <azul> !important; height: 56px }`
 *   `.MuiSvgIcon-root       { color: #212121 }`
 *   `textarea               { border: 1px solid #C4C4C4; margin-top: 10px !important }`
 *
 * Era isso que deixava o card alto (o campo `size="small"` continuava com 56px
 * e rótulo de 18px), que fazia o card de atenção sair azul em vez de laranja (o
 * `!important` do tema vence o `sx`) e que punha um ícone preto dentro do botão
 * azul. Nenhum desses três se resolve sem devolver `!important` daqui, e mexer
 * no tema consertaria o painel quebrando todas as outras telas.
 *
 * O escopo é deliberado: tudo aqui é `sx` de componente, aplicado só dentro do
 * catálogo.
 */
import {
  CONDUCCO_ORANGE,
  CONDUCCO_PRIMARY,
  INSIGHTS_BORDER,
  INSIGHTS_FONT,
  INSIGHTS_MUTED,
  INSIGHTS_TEXT,
} from "../tokens";

/** Altura real dos campos do card — o tema pede 56px, o card pede 38. */
const FIELD_HEIGHT = 38;

const FIELD_LABEL_SX = {
  "& .MuiInputLabel-root": {
    fontFamily: INSIGHTS_FONT,
    // O tema fixa 18px e fundo `#FAFAFA` no rótulo; dentro de um card branco
    // isso vira uma tarja cinza atravessando a borda. O `legend` do
    // `OutlinedInput` já recorta o espaço — o fundo é desnecessário.
    fontSize: "0.75rem !important",
    fontWeight: "600 !important",
    lineHeight: "1.4 !important",
    color: `${INSIGHTS_MUTED} !important`,
    backgroundColor: "transparent !important",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: `${CONDUCCO_PRIMARY} !important`,
  },
  // Validação em laranja, não no vermelho do MUI: o shell da marca não tem
  // vermelho (ver `tokens.ts`), e um único campo vermelho no meio do painel lê
  // como falha do sistema, não como "corrija a data".
  "& .MuiInputLabel-root.Mui-error": { color: `${CONDUCCO_ORANGE} !important` },
  "& .MuiFormHelperText-root": {
    fontFamily: INSIGHTS_FONT,
    fontSize: "0.6875rem",
    lineHeight: 1.4,
    marginLeft: 0,
    marginTop: "5px",
  },
  "& .MuiFormHelperText-root.Mui-error": { color: CONDUCCO_ORANGE },
};

const FIELD_OUTLINE_SX = {
  borderRadius: "10px",
  fontFamily: INSIGHTS_FONT,
  fontSize: "0.8125rem",
  color: INSIGHTS_TEXT,
  backgroundColor: "#FFFFFF",
  "& fieldset": { borderColor: INSIGHTS_BORDER },
  "&:hover fieldset": { borderColor: "#C4C4D4" },
  "&.Mui-focused fieldset": {
    borderColor: CONDUCCO_PRIMARY,
    borderWidth: "1px",
    boxShadow: "0 0 0 3px rgba(48,36,225,0.14)",
  },
  "&.Mui-error fieldset": { borderColor: `${CONDUCCO_ORANGE} !important` },
  "&.Mui-error.Mui-focused fieldset": {
    boxShadow: "0 0 0 3px rgba(224,113,36,0.16)",
  },
};

/** Campo de uma linha (data). */
export const COMPACT_FIELD_SX = {
  ...FIELD_LABEL_SX,
  "& .MuiOutlinedInput-root": {
    ...FIELD_OUTLINE_SX,
    height: `${FIELD_HEIGHT}px !important`,
    padding: "0 12px !important",
  },
  // `input[type=date]` do Chrome deixa o ícone do calendário na cor do tema
  // (preto); alinhado ao resto do campo ele para de puxar a atenção.
  "& input[type=date]::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
    opacity: 0.5,
    "&:hover": { opacity: 1 },
  },
};

/** Campo de várias linhas (lista de CPF). Altura livre, o resto igual. */
export const COMPACT_TEXTAREA_SX = {
  ...FIELD_LABEL_SX,
  "& .MuiOutlinedInput-root": {
    ...FIELD_OUTLINE_SX,
    height: "auto !important",
    minHeight: `${FIELD_HEIGHT}px`,
    padding: "9px 12px !important",
  },
  // O seletor global `textarea` do tema desenha uma segunda borda dentro do
  // campo e empurra o texto 10px para baixo.
  "& textarea": {
    border: "none !important",
    outline: "none !important",
    margin: "0 !important",
    padding: "0 !important",
    fontFamily: INSIGHTS_FONT,
    fontSize: "0.8125rem",
    lineHeight: 1.55,
    color: INSIGHTS_TEXT,
    backgroundColor: "transparent",
  },
};

/**
 * Rótulo de um grupo de campos — "Período de exportação".
 *
 * Existe porque o rótulo flutuante do `TextField` não serve para nomear **dois**
 * campos: ele nasce dentro de um deles, e um texto longo ali sobe até a borda e
 * colide com o que estiver acima. Como linha própria, o nome do grupo é dito uma
 * vez e os campos ficam com rótulos curtos.
 */
export const GROUP_LABEL_SX = {
  fontFamily: INSIGHTS_FONT,
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1.4,
  color: INSIGHTS_MUTED,
};

/**
 * O seletor de modo (intervalo / dia único).
 *
 * O `ToggleButtonGroup` padrão do MUI chega com raio 4px, fonte 14px em caixa
 * alta e preenchimento cinza — três coisas que o card não usa em lugar nenhum.
 * Sem isto ele lê como peça de outra tela colada aqui dentro.
 *
 * Altura 26px de propósito: é um controle **secundário**, e igualar os 38px do
 * campo o faria disputar atenção com o dado.
 */
export const MODE_TOGGLE_SX = {
  gap: "2px",
  padding: "2px",
  borderRadius: "9px",
  backgroundColor: "#F4F4F8",
  border: `1px solid ${INSIGHTS_BORDER}`,
  "& .MuiToggleButtonGroup-grouped": {
    fontFamily: INSIGHTS_FONT,
    textTransform: "none",
    fontSize: "0.6875rem",
    fontWeight: 600,
    letterSpacing: 0,
    height: "22px",
    minHeight: 0,
    padding: "0 10px",
    border: "none !important",
    borderRadius: "7px !important",
    color: INSIGHTS_MUTED,
    transition: "background-color 140ms ease-out, color 140ms ease-out",
    "&:hover": { backgroundColor: "rgba(48,36,225,0.06)", color: INSIGHTS_TEXT },
    // Anel de foco visível e **instantâneo** — anel que aparece animado
    // atrapalha quem navega por teclado.
    "&.Mui-focusVisible": {
      outline: `2px solid ${CONDUCCO_PRIMARY}`,
      outlineOffset: "-2px",
    },
    "&.Mui-selected": {
      backgroundColor: "#FFFFFF",
      color: `${CONDUCCO_PRIMARY} !important`,
      boxShadow: "0 1px 2px rgba(26,26,46,0.10)",
      "&:hover": { backgroundColor: "#FFFFFF" },
    },
    "&.Mui-disabled": { color: "#B4B4C4", border: "none !important" },
  },
};

/**
 * O botão de gerar. `accent` é a cor do relatório — e precisa de `!important`
 * para existir: sem ele o tema pinta todo `contained` de azul, e os relatórios
 * de atenção perdiam a única marca que os distinguia.
 */
export function actionButtonSx(accent: string) {
  return {
    fontFamily: INSIGHTS_FONT,
    textTransform: "none",
    fontWeight: "600 !important",
    fontSize: "0.8125rem",
    letterSpacing: 0,
    whiteSpace: "nowrap",
    height: "36px !important",
    minHeight: "36px",
    minWidth: 0,
    px: 1.75,
    borderRadius: "10px",
    boxShadow: "none",
    backgroundColor: `${accent} !important`,
    color: "#FFFFFF !important",
    // A regra global `.MuiSvgIcon-root { color: #212121 }` atinge o ícone
    // diretamente — herdar o branco do botão não basta.
    "& .MuiSvgIcon-root": { color: "#FFFFFF !important", fontSize: 17 },
    "& .MuiCircularProgress-root": { color: "#FFFFFF !important" },
    "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "7px" },
    "&:hover": {
      backgroundColor: `${accent} !important`,
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#EDEDF3 !important",
      color: "#A0A0B2 !important",
      "& .MuiSvgIcon-root": { color: "#A0A0B2 !important" },
    },
  };
}
